import React, { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, Gift, ArrowRight, Shield, Star } from "lucide-react";
import { Link } from "react-router-dom";

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

// --- Same Features for Monthly + Yearly ---
const FULL_FEATURES = [
  "Unlimited animated stories",
  "Letters & sounds (Phonics Focus)",
  "Up to 3 Child Profiles",
  "Progress dashboard",
  "Story Sketchpad",
  "Learning Hub (ABCs/123s/Words)",
  "Puzzles & mini-games",
  "Ad-free experience",
  "Parent insights & reports"
];

// --- Pricing plans ---
const PLANS = [
  {
    name: "Free Trial",
    priceUSD: 0,
    priceINR: 0,
    isPopular: false,
    badgeText: "1-Day Access",
    color: "pink",
    features: [
      "Access to 10 selected stories",
      "Basic phonics lessons",
      "1 Child Profile",
      "Limited progress dashboard",
      "Limited Sketchpad tools",
      "Mini-games (limited)",
      "Ad-supported access"
    ],
  },

  {
    name: "Monthly Plan",
    priceUSD: 7.99,
    priceINR: 699,
    isPopular: false,
    badgeText: "Best for Starting",
    color: "indigo",
    features: FULL_FEATURES,
  },

  {
    name: "Yearly Plan",
    priceUSD: 79.99,
    priceINR: 5999,
    isPopular: true,
    badgeText: "Best Value",
    color: "cyan",
    features: FULL_FEATURES,
  },

  {
    name: "Institute Plan",
    priceUSD: 199,
    priceINR: 16999,
    isPopular: false,
    badgeText: "For Schools",
    color: "emerald",
    features: [
      "All content unlocked for students",
      "Teacher admin dashboard",
      "Monitor classroom progress",
      "Worksheets & printable packs",
      "5–50 student profiles",
      "Bulk license discounts",
      "Onboarding support",
      "Premium priority support",
    ],
  },
];

const FeatureItem = ({ text }) => (
  <div className="flex items-start gap-3">
    <CheckCircle className="w-5 h-5 text-emerald-500" />
    <span className="text-sm font-medium text-gray-700">{text}</span>
  </div>
);

const SubscriptionCard = ({ plan, currency }) => {
  const price = currency === "USD" ? `$${plan.priceUSD}` : `₹${plan.priceINR}`;

  return (
    <motion.div
      variants={fadeIn}
      className="relative rounded-2xl p-7 flex flex-col h-full bg-white border shadow-lg hover:shadow-2xl transition-all"
    >
      {/* Badge */}
      <div
        className={`absolute -top-3 right-4 bg-${plan.color}-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md`}
      >
        {plan.badgeText}
      </div>

      {/* Title & Price */}
      <div className="text-center mb-6">
        <h3 className={`text-2xl font-bold text-${plan.color}-700`}>
          {plan.name}
        </h3>
        <p className="text-4xl font-extrabold mt-2">{price}</p>
        {plan.name === "Yearly Plan" ? (
          <p className="text-gray-500 text-sm">per year</p>
        ) : plan.name === "Free Trial" ? (
          <p className="text-gray-500 text-sm">1 day</p>
        ) : (
          <p className="text-gray-500 text-sm">per month</p>
        )}
      </div>

      {/* Features */}
      <div className="space-y-3 flex-grow mb-6">
        {plan.features.map((f, i) => (
          <FeatureItem key={i} text={f} />
        ))}
      </div>

      {/* Button */}
      <motion.button
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.98 }}
        className={`w-full py-3 rounded-xl font-semibold text-lg shadow-md text-white bg-${plan.color}-600 hover:bg-${plan.color}-700`}
      >
        Get Started
      </motion.button>
    </motion.div>
  );
};

export default function SubscriptionPage() {
  const [currency, setCurrency] = useState("USD");

  return (
    <motion.div initial="hidden" animate="visible" className="bg-slate-50">
      {/* HERO */}
      <div className="text-center py-20 bg-gradient-to-br from-indigo-50 to-cyan-50">
        <motion.h1 variants={fadeIn} className="text-5xl font-extrabold">
          Unlock{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-indigo-600">
            Smarter Learning
          </span>
        </motion.h1>

        <motion.p
          variants={fadeIn}
          className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto"
        >
          Unlimited stories, phonics, creativity tools & learning adventures.
        </motion.p>

        {/* Currency Switch */}
        <motion.div variants={fadeIn} className="mt-10 flex justify-center">
          <div className="bg-white shadow-md rounded-full flex gap-2 p-2 border">
            <button
              onClick={() => setCurrency("USD")}
              className={`px-5 py-2 rounded-full font-medium ${
                currency === "USD"
                  ? "bg-indigo-600 text-white"
                  : "text-gray-600"
              }`}
            >
              USD $
            </button>
            <button
              onClick={() => setCurrency("INR")}
              className={`px-5 py-2 rounded-full font-medium ${
                currency === "INR"
                  ? "bg-indigo-600 text-white"
                  : "text-gray-600"
              }`}
            >
              INR ₹
            </button>
          </div>
        </motion.div>
      </div>

      {/* PLANS GRID */}
      <div className="max-w-7xl mx-auto px-6 py-20 grid grid-cols-1 md:grid-cols-3 gap-10">
        {PLANS.map((plan, i) => (
          <SubscriptionCard key={i} plan={plan} currency={currency} />
        ))}
      </div>

      {/* WHY CHOOSE */}
      <div className="bg-white py-20">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-4xl font-bold">Why Parents Love TalkyToon</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 mt-12 text-gray-700">
            <div>
              <Star className="w-12 h-12 text-yellow-400 mx-auto" />
              <p className="font-semibold mt-3">Safe & Kid-Friendly</p>
              <p className="text-sm mt-1">No ads. No distractions.</p>
            </div>
            <div>
              <Star className="w-12 h-12 text-yellow-400 mx-auto" />
              <p className="font-semibold mt-3">Made for Learning</p>
              <p className="text-sm mt-1">Phonics, stories & early learning tools.</p>
            </div>
            <div>
              <Star className="w-12 h-12 text-yellow-400 mx-auto" />
              <p className="font-semibold mt-3">Progress Tracking</p>
              <p className="text-sm mt-1">Watch your child grow every week.</p>
            </div>
          </div>
        </div>
      </div>

      {/* SECURE PAYMENT */}
      <div className="py-16 text-center">
        <div className="inline-flex items-center gap-3 bg-white p-5 rounded-xl shadow-md border border-gray-200">
          <Shield className="w-8 h-8 text-green-500" />
          <p className="font-semibold text-gray-700">
         Secure payment • Instant activation • 100% safe checkout
          </p>
        </div>
      </div>

      {/* BACK LINK */}
      <div className="text-center pb-20">
        <Link
          to="/"
          className="inline-flex items-center text-indigo-600 font-semibold hover:text-indigo-800"
        >
          <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
          Back to Free Stories
        </Link>
      </div>
    </motion.div>
  );
}
